import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigurationService } from "../../../../core/services/configuration.service";
import { UserType } from "../../../../core/models/user-type.model";
import { ContentObserver } from '@angular/cdk/observers';
import { ActivatedRoute } from "@angular/router";
import { USER_TYPES } from "../../../../core/shared/constants";
import { BaseComponent } from "../../../../core/shared/base.component";
import Swal from "sweetalert2";
import { NgxSpinnerService } from "ngx-spinner";
import { UsersService } from '../../../../core/services/users.service';
import { LoaderService } from '../../../../core/shared/services/loader.service';
import { AuthService } from '../../../../core/services/auth.service';
import { RecoverPasswordRequest } from '../../../../core/models/auth/auth.request';
import { AppointmentsService } from '../../../../core/services/appointments.service';
import { Appointment } from '../../../../core/models/appointment.model';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-experts-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './experts-management.component.html',
  styleUrl: './experts-management.component.scss'
})
export class ExpertsManagementComponent extends BaseComponent implements OnInit {

  usersBase: any[] = [];
  users: any[] = [];
  totalTodosUsuarios: number = 0; // Total de usuarios
  loading = false;

  userTypeId = USER_TYPES.Expert.id;
  pageTitle = "Administración de Expertos";

  expertCategories: any[] = []; // Lista de categorías de expertos para el select

  showModalForm = false; // Controla la visibilidad del modal
  editMode: boolean = false; // Indica si estamos en modo edición
  usuarioFormGroup!: FormGroup;

  activeTab: any = 'all'; // Tab activo por defecto
  searchTerm: any;
  selectedUserId: string = ''; // ID del usuario seleccionado para edición

  source: string = ''; // Fuente de los usuarios

  // Variables para el modal de citas
  showAppointmentsModal = false;
  selectedExpert: any = null;
  expertAppointments: Appointment[] = [];
  loadingAppointments = false;

  private userService = inject(UsersService);
  private configService = inject(ConfigurationService);
  private loader = inject(LoaderService);
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private spinner = inject(NgxSpinnerService);
  private formBuilder = inject(FormBuilder); // FormBuilder para crear formularios reactivos
  private appointmentsService = inject(AppointmentsService);


  constructor() {
    super();
  }

  ngOnInit(): void {

    this.route.data.subscribe(data => {
      this.userTypeId = data['userTypeId'];
      this.pageTitle = data['pageTitle'];

      this.loadUsers(); // sin paginación
      this.loadExpertCategories(); // Cargar tipos de expertos     

    });

    this.crearFormulario();

  }

  crearFormulario(): void {
    this.usuarioFormGroup = this.formBuilder.group({
      FULLNAME: ['', Validators.required],
      EMAIL: ['', [Validators.required, Validators.email]],
      CODETELEFONO: ['51'],
      TELEFONO: [''],
      FECHAINGRESO: [new Date().toISOString().substring(0, 10), Validators.required],
      CATEGORIASEXPERTO: [[], [Validators.required, this.arrayNotEmptyValidator]], // Array de categorías
      COST: ['', [Validators.required, Validators.min(0.01)]], // Costo por hora
      PUBLICADO: [true], // Estado de publicación (true = publicado, false = no publicado)
    });
  }

  // Validador personalizado para asegurar que el array no esté vacío
  arrayNotEmptyValidator(control: any) {
    const value = control.value;
    if (!value || !Array.isArray(value) || value.length === 0) {
      return { required: true };
    }
    return null;
  }

  // Método para comparar valores en el select multiple
  compareCategoryIds(cat1: any, cat2: any): boolean {
    return cat1 && cat2 ? cat1.toString() === cat2.toString() : cat1 === cat2;
  }
  // getter para un fácil acceso a los campos de formulario
  get frmUsuario(): any { return this.usuarioFormGroup.controls; } // get de los controles

  // Método para obtener las categorías de un usuario para mostrar en la tabla
  getUserCategories(user: any): any[] {
    if (user.type?.expertCategories && Array.isArray(user.type.expertCategories)) {
      return user.type.expertCategories;
    } else if (user.type?.expertCategory) {
      return [user.type.expertCategory];
    }
    return [];
  }

  loadUsers() {
    this.loading = true;
    this.loader.show();

    this.userService.find(this.userTypeId, '').subscribe({
      next: (response: any) => {
        this.usersBase = response.data;
        this.usersBase = this.usersBase.map((user: any) => {

          // Agregar propiedad para el ngModel del select
          user.selectedSubscriptionId = user.type?.suscriptionType?.id?.toString() || '0';
          user.selectedCoachTypeId = user.type?.coachType?.id || 0;
          // Convertir expertCategory a array si existe, o usar array vacío
          if (user.type?.expertCategory) {
            user.selectedExpertCategoryIds = [user.type.expertCategory.id.toString()];
          } else if (user.type?.expertCategories && Array.isArray(user.type.expertCategories)) {
            user.selectedExpertCategoryIds = user.type.expertCategories.map((cat: any) => cat.id.toString());
          } else {
            user.selectedExpertCategoryIds = [];
          }

          if (user.type?.expertCategory || user.type?.expertCategories) {
            console.log('user.type?.expertCategory/expertCategories', user.type?.expertCategory || user.type?.expertCategories);
          }

          return user;
        });

        this.totalTodosUsuarios = this.usersBase.length; // Total de usuarios sin paginación
        this.users = this.usersBase;
        this.loading = false;
        this.loader.hide();
      },
      error: (error) => {
        console.error('Error cargando usuarios:', error);
        this.loading = false;
        this.loader.hide();
      }
    });
  }

  onCategoryChange(user: any, selectedIds: string[]) {
    // Guardar la selección anterior para poder revertir si es necesario
    const previousSelection = [...(user.selectedExpertCategoryIds || [])];
    
    // Actualizar temporalmente la selección para que se vea en el UI
    user.selectedExpertCategoryIds = selectedIds || [];
    
    const selectedCategories = this.expertCategories.filter(cat => selectedIds.includes(cat.id.toString()));
    
    if (selectedCategories.length > 0 && user) {
      const categoryNames = selectedCategories.map(cat => cat.name).join(', ');
      
      Swal.fire({
        html: `Va a actualizar las categorías de experto de <strong>${user.name}</strong> a: <strong>${categoryNames}</strong>. ¿Desea continuar?`,
        title: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          this.spinner.show();
          const categoryIds = selectedIds.map((id: string) => Number(id));
          this.userService.updateExperCategories(user._id, categoryIds).subscribe({
            next: (res) => {
              this.spinner.hide();
              // Actualizar el objeto user con las nuevas categorías
              if (res.data?.type?.expertCategories) {
                user.type.expertCategories = res.data.type.expertCategories;
              } else if (res.data?.type?.expertCategory) {
                // Si el backend aún devuelve expertCategory singular, convertirlo a array
                user.type.expertCategories = [res.data.type.expertCategory];
              }
              user.selectedExpertCategoryIds = selectedIds;
            },
            error: () => {
              this.spinner.hide();
              // Revertir la selección en caso de error
              user.selectedExpertCategoryIds = previousSelection;
            }
          });
        } else {
          // Revertir la selección si el usuario cancela
          user.selectedExpertCategoryIds = previousSelection;
        }
      });
    } else if (selectedIds.length === 0) {
      // Si no hay selección, mostrar mensaje
      Swal.fire({
        title: 'Atención',
        text: 'Debe seleccionar al menos una categoría',
        icon: 'warning',
        confirmButtonText: 'Aceptar'
      });
      // Revertir la selección
      user.selectedExpertCategoryIds = previousSelection;
    }
  }

  updateExpertCategory(user: any, event: any) {
    // Este método ya no se usa, pero lo mantengo por compatibilidad
    const selectedOptions = Array.from(event.target.selectedOptions, (option: any) => option.value);
    this.onCategoryChange(user, selectedOptions);
  }

  loadExpertCategories() {
    this.configService.getExpertCategories().subscribe({
      next: (response: any) => {
        this.expertCategories = response.data;
      },
      error: (error) => {
        console.error('Error cargando categorías de expertos:', error);
      }
    });
  }


  changeAll() {
    this.activeTab = 'all';
    this.loadUsers(); // Recargar usuarios para asegurarse de que se muestren todos
    this.searchTerm = ''; // Limpiar el término de búsqueda
  }

  changeNew() {
    this.activeTab = 'new';
    this.searchTerm = ''; // Limpiar el término de búsqueda
    // filtrar usuarios por createdAt
    this.users = this.users.filter(user => {
      const createdAt = new Date(user.createdAt);
      const today = new Date();
      const diffTime = Math.abs(today.getTime() - createdAt.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 1; // Mostrar usuarios creados en los últimos 1 días
    });

  }

  newAccount() {
    this.showModalForm = true; // Mostrar el modal para crear una nueva cuenta
    this.editMode = false; // Desactivar el modo edición
    this.source = 'local'; // Asignar la fuente de la nueva cuenta
    // Implementar la lógica para crear una nueva cuenta
    // Por ejemplo, podrías abrir un formulario para ingresar los datos de la nueva cuenta
    console.log("Crear nueva cuenta");
    this.usuarioFormGroup.patchValue({
      FULLNAME: '',
      EMAIL: '',
      TELEFONO: '',
      FECHAINGRESO: new Date().toISOString().substring(0, 10),
      CATEGORIASEXPERTO: [], // Inicializar array vacío
      COST: '', // Inicializar campo de costo por hora
      PUBLICADO: true, // Por defecto publicado
    });
    // this.usuarioFormGroup.markAsPristine();
    // this.usuarioFormGroup.markAsUntouched();
  }

  // busqueda usuario
  searchUsers() {
    const term = this.searchTerm.toLowerCase().trim();

    if (!term || term.trim() === '') {
      // Si la búsqueda está vacía, mostrar todos los usuarios
      this.loadUsers();
      return;
    }
    // Filtrar usuarios por nombre
    this.users = this.users.filter(user => {
      const userName = user.name ? user.name.toLowerCase() : '';
      return userName.includes(term);
    }
    );
    console.log('Usuarios filtrados:', this.users);
  }

  closeModalForm() {
    this.showModalForm = false; // Ocultar el modal
  }

  guardarUsuario() {
    if (this.usuarioFormGroup.invalid) {
      console.error("Formulario inválido");
      console.error("Errores del formulario:", this.usuarioFormGroup.errors);
      console.error("Errores de CATEGORIASEXPERTO:", this.frmUsuario.CATEGORIASEXPERTO.errors);
      return;
    }

    const selectedCategoryIds = this.frmUsuario.CATEGORIASEXPERTO.value as string[];
    console.log("Categorías seleccionadas:", selectedCategoryIds);
    const selectedCategories = this.expertCategories.filter(cat => selectedCategoryIds.includes(cat.id.toString()));
    const fullName: string = this.frmUsuario.FULLNAME.value;
    const cost = this.frmUsuario.COST.value; // Costo por hora del experto
    
    const usuarioData = {
      name: fullName,
      firstName: fullName,
      lastName: '',
      email: this.frmUsuario.EMAIL.value,
      source: this.source,
      phone: this.frmUsuario.TELEFONO.value,
      codePhone: this.frmUsuario.CODETELEFONO.value,
      createdAt: this.frmUsuario.FECHAINGRESO.value,
      type: {
        ...USER_TYPES.Expert, // Asignar el tipo de usuario Expert
        expertCategories: selectedCategories.map(cat => ({
          id: Number(cat.id),
          name: cat.name,
          cost: cost, // Costo por hora del experto (aplicado a todas las categorías)
        })),
        // Mantener compatibilidad con expertCategory (usar la primera categoría)
        expertCategory: selectedCategories.length > 0 ? {
          id: Number(selectedCategories[0].id),
          name: selectedCategories[0].name,
          cost: cost,
        } : undefined,
      },
      status: {
        id: 2,
        name: 'Activo',
      },
      publicado: this.frmUsuario.PUBLICADO.value, // Estado de publicación
    };


    // Aquí puedes llamar al servicio para guardar el usuario
    console.log("Datos del usuario a guardar:", usuarioData);

    if (this.editMode) {
      // Si estamos en modo edición, actualizar el usuario existente
      console.log('this.selectedUserId', this.selectedUserId)
      this.userService.updateUser(this.selectedUserId, usuarioData).subscribe({
        next: (response: any) => {
          console.log('Usuario actualizado:', response.data);
          this.loadUsers(); // Recargar la lista de usuarios después de actualizar
          // Cerrar el modal después de actualizar
          this.closeModalForm();
        },
        error: (error) => {
          console.error('Error actualizando usuario:', error);
        }
      });
    }
    else {
      // Si no estamos en modo edición, crear un nuevo usuario
      // Implementa la lógica para guardar el usuario
    this.userService.createUser(usuarioData).subscribe({
      next: (response: any) => {
        console.log('GUARDADO:', response.data);
        const email = this.frmUsuario.EMAIL.value;
        if (email) {
          this.sendRecoveryEmail(email);
        }
        this.loadUsers(); // Recargar la lista de usuarios después de guardar
        // Cerrar el modal después de guardar
        this.closeModalForm();
      },
      error: (error) => {
        console.error('Error:', error);
      }
    });
    }
    // Cerrar el modal después de guardar
    // this.closeModalForm();
  }

  editarUsuario(usuario: any) {
    this.editMode = true; // Activar el modo edición
    this.showModalForm = true; // Mostrar el modal de edición
    console.log('Editar usuario:', usuario);
    this.selectedUserId = usuario._id; // Guardar el ID del usuario seleccionado
    this.source = usuario.source; // Asignar la fuente del usuario 
    console.log('Fuente del usuario:', this.source);
    const fullName = usuario.name || [usuario.firstName, usuario.lastName].filter(Boolean).join(' ').trim();
    // Obtener categorías seleccionadas (puede ser array o objeto único) - como strings para el select
    let selectedCategoryIds: string[] = [];
    if (usuario.type?.expertCategories && Array.isArray(usuario.type.expertCategories)) {
      selectedCategoryIds = usuario.type.expertCategories.map((cat: any) => cat.id.toString());
    } else if (usuario.type?.expertCategory) {
      selectedCategoryIds = [usuario.type.expertCategory.id.toString()];
    }
    
    // Obtener el costo (puede estar en la primera categoría o en expertCategory)
    const cost = usuario.type?.expertCategories?.[0]?.cost || usuario.type?.expertCategory?.cost || '';
    
    // Obtener el estado de publicación (por defecto true si no existe)
    const publicado = usuario.publicado !== undefined ? usuario.publicado : true;
    
    // Establecer los valores del formulario
    this.usuarioFormGroup.patchValue({
      FULLNAME: fullName,
      EMAIL: usuario.email,
      TELEFONO: usuario.phone,
      CODETELEFONO: usuario.codePhone || '51', // Asignar código de teléfono por defecto si no existe
      FECHAINGRESO: usuario.createdAt ? new Date(usuario.createdAt).toISOString().substring(0, 10) : new Date().toISOString().substring(0, 10),
      COST: cost, // Costo por hora del experto
      PUBLICADO: publicado // Estado de publicación
      // TIPOMENTOR: usuario.type?.coachType?.id ? usuario.type.coachType.id.toString() : '0', // Asignar ID de tipo de mentor o 0 por defecto  
    });
    
    // Establecer las categorías por separado para asegurar que se actualicen correctamente
    const categoriasControl = this.usuarioFormGroup.get('CATEGORIASEXPERTO');
    if (categoriasControl) {
      categoriasControl.setValue(selectedCategoryIds);
    }
  }

  private sendRecoveryEmail(email: string): void {
    if (!email) {
      return;
    }

    const request: RecoverPasswordRequest = { email };

    this.loader.show();
    this.authService.recoverPassword(request).subscribe({
      next: (res: any) => {
        this.loader.hide();
        if (res?.success) {
          Swal.fire({
            title: '¡Instrucciones enviadas!',
            html: `
              <p>${res.message}</p>
              <p style="font-size: 0.9em; color: gray; margin-top: 10px;">
                ¿No recibiste el mensaje? Verifica en el filtro de spam o inténtalo con otro correo electrónico.
              </p>
            `,
            icon: 'success',
            confirmButtonText: 'Continuar',
            confirmButtonColor: '#ce0024'
          });
        } else {
          const message = res?.message || 'No pudimos enviar las instrucciones. Intenta nuevamente.';
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: message,
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#ce0024'
          });
        }
      },
      error: (err) => {
        this.loader.hide();
        console.error('Error al enviar correo de recuperación:', err);
        const message = err.error?.message || 'Ocurrió un problema al enviar las instrucciones de recuperación.';
        Swal.fire({
          icon: 'error',
          title: 'Error al enviar',
          text: message,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#ce0024'
        });
      }
    });
  }

  /**
   * Abre el modal para ver las citas del experto
   */
  viewExpertAppointments(user: any): void {
    this.selectedExpert = user;
    this.showAppointmentsModal = true;
    this.loadingAppointments = true;
    this.expertAppointments = [];

    // Obtener el ID del experto (puede ser _id o id)
    const expertId = user._id || user.id;

    if (!expertId) {
      console.error('No se pudo obtener el ID del experto');
      this.loadingAppointments = false;
      return;
    }

    this.appointmentsService.findByCoach(expertId).subscribe({
      next: (response: any) => {
        this.expertAppointments = response.data || [];
        // Ordenar citas por fecha (más recientes primero)
        this.expertAppointments.sort((a, b) => {
          const dateA = new Date(a.date + ' ' + a.time).getTime();
          const dateB = new Date(b.date + ' ' + b.time).getTime();
          return dateB - dateA;
        });
        this.loadingAppointments = false;
      },
      error: (error) => {
        console.error('Error cargando citas del experto:', error);
        this.loadingAppointments = false;
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar las citas del experto',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#ce0024'
        });
      }
    });
  }

  /**
   * Cierra el modal de citas
   */
  closeAppointmentsModal(): void {
    this.showAppointmentsModal = false;
    this.selectedExpert = null;
    this.expertAppointments = [];
  }

  /**
   * Verifica si una cita ya fue realizada (fecha pasada)
   */
  isAppointmentCompleted(appointment: Appointment): boolean {
    const appointmentDate = new Date(appointment.date + ' ' + appointment.time);
    const now = new Date();
    return appointmentDate < now;
  }

  /**
   * Descargar reporte PDF con todas las citas del experto
   */
  async downloadExpertAppointments(user: any): Promise<void> {
    try {
      this.loader.show();
      
      // Obtener el ID del experto
      const expertId = user._id || user.id;

      if (!expertId) {
        console.error('No se pudo obtener el ID del experto');
        this.loader.hide();
        return;
      }

      // Obtener todas las citas del experto
      this.appointmentsService.findByCoach(expertId).subscribe({
        next: async (response: any) => {
          const appointments = response.data || [];
          
          if (appointments.length === 0) {
            this.loader.hide();
            Swal.fire({
              icon: 'info',
              title: 'Sin citas',
              text: 'No hay citas registradas para este experto',
              confirmButtonText: 'Aceptar',
              confirmButtonColor: '#ce0024'
            });
            return;
          }

          // Ordenar citas por fecha (más antiguas primero para el reporte)
          const sortedAppointments = appointments.sort((a: Appointment, b: Appointment) => {
            const dateA = new Date(a.date + ' ' + a.time).getTime();
            const dateB = new Date(b.date + ' ' + b.time).getTime();
            return dateA - dateB;
          });

          // Generar PDF
          await this.generateAppointmentsPDF(user, sortedAppointments);
          this.loader.hide();
        },
        error: (error) => {
          console.error('Error cargando citas del experto:', error);
          this.loader.hide();
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudieron cargar las citas del experto',
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#ce0024'
          });
        }
      });
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      this.loader.hide();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al generar el reporte. Por favor, inténtalo de nuevo.',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#ce0024'
      });
    }
  }

  /**
   * Generar PDF con las citas del experto
   */
  private async generateAppointmentsPDF(user: any, appointments: Appointment[]): Promise<void> {
    try {
      // Crear documento PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      let currentY = 10;
      const margin = 14;

      // Cargar y añadir el logo
      try {
        const response = await fetch("assets/img/phoenix-logo.png");
        const blob = await response.blob();
        const base64data = await this.blobToBase64(blob);
        doc.addImage(base64data, "PNG", margin, currentY, 60, 10);
        currentY += 20;
      } catch (error) {
        console.warn('No se pudo cargar el logo:', error);
        currentY += 10;
      }

      // Información de la empresa
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text("R.U.C. N.o 20609872463", pageWidth - margin, currentY, { align: 'right' });
      currentY += 5;
      doc.text("PHOENIX GROUP S.A.C.", pageWidth - margin, currentY, { align: 'right' });
      currentY += 5;
      doc.text("San Isidro, Lima, Perú", pageWidth - margin, currentY, { align: 'right' });
      currentY += 15;

      // Título del reporte
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text("Reporte de Citas", margin, currentY);
      currentY += 10;

      // Información del experto
      doc.setFontSize(12);
      doc.setTextColor(50, 50, 50);
      doc.setFont('helvetica', 'normal');
      doc.text(`Experto: ${user.name || 'N/A'}`, margin, currentY);
      currentY += 7;
      doc.text(`Email: ${user.email || 'N/A'}`, margin, currentY);
      currentY += 7;
      doc.text(`Total de citas: ${appointments.length}`, margin, currentY);
      currentY += 15;

      // Tabla de citas
      const tableData = appointments.map((appointment, index) => {
        const formattedDate = this.formatDateForPdf(appointment.date);
        const formattedTime = appointment.time || 'N/A';
        const customerName = appointment.customer?.name || 'N/A';
        const duration = appointment.duration ? `${appointment.duration} min` : 'N/A';
        const status = this.isAppointmentCompleted(appointment) ? 'Realizada' : 'Pendiente';
        const notes = appointment.notes ? 'Sí' : 'No';

        return [
          (index + 1).toString(),
          formattedDate,
          formattedTime,
          customerName,
          duration,
          status,
          notes
        ];
      });

      // Agregar tabla
      autoTable(doc, {
        startY: currentY,
        head: [['#', 'Fecha', 'Hora', 'Cliente', 'Duración', 'Estado', 'Notas']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [220, 53, 69], // Rojo similar al tema
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 2
        },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },   // #
          1: { cellWidth: 35, halign: 'left' },     // Fecha
          2: { cellWidth: 20, halign: 'center' },    // Hora
          3: { cellWidth: 50, halign: 'left' },      // Cliente
          4: { cellWidth: 20, halign: 'center' },    // Duración
          5: { cellWidth: 25, halign: 'center' },    // Estado
          6: { cellWidth: 15, halign: 'center' }      // Notas
        },
        margin: { left: margin, right: margin },
        styles: {
          cellPadding: 2,
          overflow: 'linebreak',
          font: 'helvetica',
          fontSize: 8
        },
        didParseCell: function (data: any) {
          // Truncar texto largo en la columna Cliente si es necesario
          if (data.column.index === 3 && data.cell.text) {
            const text = Array.isArray(data.cell.text) ? data.cell.text[0] : data.cell.text;
            if (text && text.length > 25) {
              data.cell.text = [text.substring(0, 23) + '...'];
            }
          }
        }
      });

      // Obtener la posición Y después de la tabla
      const finalY = (doc as any).lastAutoTable.finalY || currentY + 50;
      currentY = finalY + 15;

      // Verificar si necesitamos una nueva página para las notas
      if (currentY > pageHeight - 50) {
        doc.addPage();
        currentY = 20;
      }

      // Agregar sección de notas detalladas si hay citas con notas
      const appointmentsWithNotes = appointments.filter(apt => apt.notes);
      if (appointmentsWithNotes.length > 0) {
        // Línea separadora
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 15;

        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text("Notas de las Citas", margin, currentY);
        currentY += 12;

        for (const appointment of appointmentsWithNotes) {
          // Verificar si necesitamos una nueva página
          if (currentY > pageHeight - 60) {
            doc.addPage();
            currentY = 20;
          }

          const appointmentDate = new Date(appointment.date);
          const formattedDate = this.formatDateForPdf(appointment.date);
          const customerName = appointment.customer?.name || 'N/A';

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          doc.text(`${formattedDate} - ${customerName}`, margin, currentY);
          currentY += 8;

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.setTextColor(50, 50, 50);
          
          // Limpiar HTML de las notas si existe
          const notesText = this.stripHtml(appointment.notes || '');
          const splitNotes = doc.splitTextToSize(notesText, pageWidth - 2 * margin);
          doc.text(splitNotes, margin, currentY);
          currentY += splitNotes.length * 5 + 10;
        }
      }

      // Generar nombre del archivo
      const expertName = (user.name || 'Experto').replace(/\s+/g, '_');
      const currentDate = new Date();
      const dateStr = `${currentDate.getDate()}_${currentDate.getMonth() + 1}_${currentDate.getFullYear()}`;
      const fileName = `Reporte_Citas_${expertName}_${dateStr}.pdf`;

      // Guardar PDF
      doc.save(fileName);
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      throw error;
    }
  }

  /**
   * Convertir blob a base64
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Formatear fecha para mostrar en el PDF
   */
  private formatDateForPdf(date: string): string {
    const monthNames = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    return `${day} de ${month} ${year}`;
  }

  /**
   * Remover HTML de las notas
   */
  private stripHtml(html: string): string {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

}
